import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api";
import { defaultCollectionCfg } from "@/lib/collections";

// Personal per-collection display preferences (density + visible/ordered
// properties + sort), persisted through the EXISTING /api/preferences store
// under `collection_props[pageKey]`. No second preference architecture.
export function useCollectionPrefs(pageKey, def) {
  const [cfg, setCfg] = useState(() => defaultCollectionCfg(def));
  const [loaded, setLoaded] = useState(false);
  const ref = useRef({});
  const timer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const prefs = await apiFetch("/preferences");
        ref.current = prefs || {};
        const saved = prefs?.collection_props?.[pageKey];
        if (saved) setCfg((c) => ({ ...c, ...saved }));
      } catch { /* keep defaults */ } finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [pageKey]);

  const update = useCallback((patch) => {
    setCfg((c) => {
      const next = typeof patch === "function" ? patch(c) : { ...c, ...patch };
      clearTimeout(timer.current);
      const collection_props = { ...(ref.current.collection_props || {}), [pageKey]: next };
      ref.current = { ...ref.current, collection_props };
      pending.current = { collection_props };
      timer.current = setTimeout(() => {
        pending.current = null;
        apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { collection_props } }) }).catch(() => {});
      }, 500);
      return next;
    });
  }, [pageKey]);

  // Flush a pending change on unmount so a fast navigation never drops the edit.
  useEffect(() => () => {
    clearTimeout(timer.current);
    if (pending.current) {
      apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: pending.current }) }).catch(() => {});
      pending.current = null;
    }
  }, []);

  return { cfg, update, loaded };
}
